// ⚠️ BANCO DE PRUEBAS — VARIANTE v2 DEL MODO MODELO. TEMPORAL.
//
// La grilla de fondos del panel lateral de `/interfaz-v2/modo/modelo-v2`.
//
// Reemplaza al carrusel de CARPETAS del `FondoPickerInline`, donde cada categoría era una
// card grande y tocarla te sacaba del panel a un sheet full-screen filtrado. Acá las
// categorías son PILLS arriba —igual que en el picker de poses— y los fondos se eligen en
// la misma caja, sin salir a ningún lado.
// Ver ADR-2026-08-25-fondos-con-pills-como-poses.
//
// Sólo lo monta `PresetDetailSheetModelo`. Se borra al cerrar el rediseño; el
// `FondoPickerInline` de producción sigue como está.

import React from 'react';
import {
  Check,
  ChevronLeft,
  Sparkles,
  Type as TypeIcon,
  Upload,
} from 'lucide-react';
import CachedImg from '../../../components/CachedImg';
import { AnimatedBlurBg } from '../AnimatedBlurBg';
// `DescribeView` es el MISMO formulario del picker de producción —textarea, recientes,
// "Sugerime ideas" y el chat con el agente—, no una copia: describir el fondo tiene que
// funcionar igual se entre por donde se entre
// (ADR-2026-09-02-describir-fondo-vuelve-al-chat).
import {
  AjustesFondoBlanco,
  DescribeView,
  type FondoSelection,
} from '../FondoPicker';
import { alElegirLiso, etiquetaDelLiso, hexDelLiso } from '../coloresDeFondoLiso';
import type { FondoBiblioteca, FondoGuardado } from '../fondosApi';
// La subida y la card de fondo con foto son las MISMAS que usa "Ambiente completo" en su
// panel de Ambiente (ADR-2026-09-28-ambiente-completo-fondo-propio).
import { CardFondo, SubirFondoView } from '../components/SubirFondoView';

/** Los fondos propios del cliente, que no viven en la biblioteca. */
const MIS_FONDOS = '__mis_fondos__';

export interface FondoGrillaProps {
  selection: FondoSelection | null;
  onChange: (sel: FondoSelection) => void;
  biblioteca: FondoBiblioteca[];
  guardados: FondoGuardado[];
  /**
   * La empresa dueña del fondo que se sube. Sin ella no hay dónde guardarlo: la vista
   * "subir" avisa y no intenta.
   */
  companyId: string | null;
  /** Se llama al terminar un alta, para que el padre recargue "Mis fondos". */
  onFondoSaved: () => void;
  /**
   * Productos elegidos y rubro — el contexto que necesita "Sugerime ideas" para proponer
   * escenarios que tengan que ver con lo que se está fotografiando.
   */
  selectedProductNames?: string[];
  rubro?: string | null;
  /** Se llama al APLICAR una descripción: el panel se cierra, como al confirmar. */
  onAplicar?: () => void;
}

/** Tarjeta de una opción sin foto (describir / subir). */
const CardAccion: React.FC<{
  label: string;
  icono: React.ReactNode;
  elegida?: boolean;
  onClick: () => void;
}> = ({ label, icono, elegida, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`h-[var(--alto-fila,150px)] w-full rounded-2xl border-2 border-dashed transition-colors flex flex-col items-center justify-center gap-2 px-2 ${
      elegida
        ? 'border-brand-primary bg-brand-primary/[0.04]'
        : 'border-neutral-200 hover:border-brand-primary/40 bg-white'
    }`}
  >
    <span className="w-9 h-9 rounded-full bg-black/[0.04] grid place-items-center text-brand-black/45">
      {icono}
    </span>
    <span className="text-[12.5px] font-medium text-brand-black/70 text-center leading-tight">
      {label}
    </span>
  </button>
);

/**
 * Tarjeta de una CARPETA de la biblioteca: su tapa, su nombre y cuántos fondos tiene.
 *
 * Es la misma anatomía que las carpetas del picker de siempre —foto de fondo, nombre y
 * "N fondos" sobre un degradado—, para que abrir una se lea como entrar y no como elegir
 * ese fondo (ADR-2026-09-02-los-fondos-de-atlas-vuelven-a-carpetas).
 */
const CardCarpeta: React.FC<{
  label: string;
  count: number;
  tapa: string;
  tieneLoElegido: boolean;
  onClick: () => void;
}> = ({ label, count, tapa, tieneLoElegido, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`relative h-[var(--alto-fila,150px)] w-full rounded-2xl overflow-hidden bg-neutral-100 border-2 transition-all active:scale-[0.97] ${
      tieneLoElegido ? 'border-brand-primary' : 'border-neutral-200'
    }`}
  >
    {tapa && (
      <CachedImg
        src={tapa}
        alt={label}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover"
      />
    )}
    {tieneLoElegido && (
      <span className="absolute top-2 right-2 z-10 inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand-primary text-white shadow">
        <Check className="w-3.5 h-3.5" strokeWidth={3} />
      </span>
    )}
    <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent px-3 pt-8 pb-2 text-left">
      <span className="block text-[13.5px] font-semibold text-white leading-tight truncate drop-shadow">
        {label}
      </span>
      <span className="block text-[11.5px] text-white/75 leading-tight drop-shadow">
        {count} {count === 1 ? 'fondo' : 'fondos'}
      </span>
    </span>
  </button>
);

export const FondoGrilla: React.FC<FondoGrillaProps> = ({
  selection,
  onChange,
  biblioteca,
  guardados,
  companyId,
  onFondoSaved,
  selectedProductNames = [],
  rubro = null,
  onAplicar,
}) => {
  /**
   * La carpeta abierta, o `null` en la portada.
   *
   * Antes esto era la pill activa de una fila de filtros arriba de la grilla. La
   * biblioteca vuelve a mostrarse como CARPETAS —una card por categoría, con su tapa y
   * cuántos fondos tiene—, así que ya no hay un filtro puesto sobre una lista: hay una
   * portada con carpetas y adentro de cada una, sus fondos
   * (ADR-2026-09-02-los-fondos-de-atlas-vuelven-a-carpetas).
   */
  const [carpeta, setCarpeta] = React.useState<string | null>(null);
  /** Vista interna: la portada, una carpeta abierta, el alta de un fondo o la descripción. */
  const [vista, setVista] = React.useState<
    'lista' | 'carpeta' | 'subir' | 'describir'
  >('lista');
  /**
   * Si el `DescribeView` está mostrando el chat con el agente. Lo maneja el padre
   * porque la vista lo resetea al entrar y salir: volver a la grilla y describir de
   * nuevo tiene que arrancar en el formulario, no en la conversación anterior.
   */
  const [describeChatMode, setDescribeChatMode] = React.useState(false);
  React.useEffect(() => {
    if (vista !== 'describir') setDescribeChatMode(false);
  }, [vista]);
  /**
   * El texto ya aplicado — con lo que arranca el formulario al volver a abrirlo.
   *
   * No hay borrador acá: describir el fondo pasó a ser una VISTA propia del panel
   * (`DescribeView`), que maneja su texto adentro y sólo avisa al confirmar. La
   * selección se toca únicamente ahí (ADR-2026-09-02-describir-fondo-vuelve-al-chat).
   */
  const textoAplicado = selection?.mode === 'descripcion' ? selection.text : '';

  /**
   * Las CARPETAS de la portada: "Mis fondos" y una por categoría de la biblioteca.
   *
   * Salen de los datos y no de una lista fija: la biblioteca se filtra por rubro, así que
   * qué categorías hay depende de la empresa. "Mis fondos" sólo aparece si tiene alguno
   * —una carpeta que siempre abre vacía no es una opción, es un callejón—. La tapa es el
   * primer fondo de la carpeta: una card con una foto adentro dice qué hay ahí mucho
   * antes que su nombre.
   */
  const carpetas = React.useMemo(() => {
    const porCategoria = new Map<string, { count: number; tapa: string }>();
    for (const f of biblioteca) {
      const key = f.categoria || 'Otros';
      const entrada = porCategoria.get(key);
      if (entrada) entrada.count += 1;
      else porCategoria.set(key, { count: 1, tapa: f.imageUrl });
    }
    return [
      ...(guardados.length > 0
        ? [
            {
              id: MIS_FONDOS,
              label: 'Mis fondos',
              count: guardados.length,
              tapa: guardados[0]?.imageUrl || '',
            },
          ]
        : []),
      ...[...porCategoria.entries()]
        .map(([id, v]) => ({ id, label: id, count: v.count, tapa: v.tapa }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ];
  }, [biblioteca, guardados]);

  /**
   * En qué carpeta vive el fondo elegido — para marcarla en la portada.
   *
   * Sin esto, elegir un fondo adentro de una carpeta y volver dejaba la portada sin
   * ninguna señal de qué está puesto: las cards de la portada son carpetas, no fondos,
   * así que el check no lo pone ninguna sola.
   */
  const carpetaElegida = React.useMemo(() => {
    if (selection?.mode === 'guardado') return MIS_FONDOS;
    if (selection?.mode === 'biblioteca') {
      const f = biblioteca.find((x) => x.id === selection.id);
      return f ? f.categoria || 'Otros' : null;
    }
    return null;
  }, [selection, biblioteca]);

  /** Los fondos de la carpeta abierta. */
  const fondosDeLaCarpeta = React.useMemo(() => {
    if (carpeta === null || carpeta === MIS_FONDOS) return [];
    return biblioteca.filter((f) => (f.categoria || 'Otros') === carpeta);
  }, [biblioteca, carpeta]);

  const abrirCarpeta = (id: string) => {
    setCarpeta(id);
    setVista('carpeta');
  };

  const esAuto = !selection || selection.mode === 'auto';

  if (vista === 'describir') {
    // Describir el fondo es una VISTA propia del panel —como subir un fondo—, no un
    // campo desplegado entre las cards: es un formulario con su chat, y metido en un
    // renglón de la grilla no entraba ni el asistente ni la conversación, así que el
    // botón de ideas terminaba dando una lista suelta en vez del chat que da el picker
    // de producción (ADR-2026-09-02-describir-fondo-vuelve-al-chat).
    return (
      <div className="flex flex-col gap-3 h-full min-h-full animate-mode-swap">
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => setVista('lista')}
            className="w-8 h-8 shrink-0 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
            aria-label="Volver"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <p className="text-[16px] font-semibold text-brand-black/90">
            Describí el fondo
          </p>
        </div>
        {/* El MISMO formulario del sheet de producción: textarea con "Recientes", el
            botón de ideas del agente y, al tocarlo, el chat para afinarla. */}
        <div className="flex-1 min-h-0 flex flex-col">
          <DescribeView
            initial={textoAplicado}
            // "Cancelar" vuelve a la grilla, no cierra el panel: se sigue eligiendo el
            // fondo, sólo se abandonó esta forma de elegirlo.
            onBack={() => setVista('lista')}
            onConfirm={(text) => {
              const limpio = text.trim();
              if (!limpio) return;
              onChange({ mode: 'descripcion', text: limpio });
              setVista('lista');
              // Confirmar la descripción CIERRA el panel, igual que antes: es el final
              // de la decisión.
              onAplicar?.();
            }}
            selectedProductNames={selectedProductNames}
            rubro={rubro}
            chatMode={describeChatMode}
            onChatModeChange={setDescribeChatMode}
          />
        </div>
      </div>
    );
  }

  if (vista === 'subir') {
    // La MISMA vista que "Ambiente completo": sube y avisa; acá el fondo nuevo queda
    // ELEGIDO y se vuelve a la carpeta "Mis fondos", donde acaba de quedar (y marcado):
    // dejar la portada obligaría a entrar a buscar lo que uno acaba de subir.
    return (
      <SubirFondoView
        companyId={companyId}
        onVolver={() => setVista('lista')}
        onSubido={(saved) => {
          onFondoSaved();
          onChange({
            mode: 'guardado',
            fileName: saved.fileName,
            name: saved.nombre,
            imageUrl: saved.imageUrl,
          });
          setCarpeta(MIS_FONDOS);
          setVista('carpeta');
        }}
      />
    );
  }

  if (vista === 'carpeta' && carpeta !== null) {
    // Adentro de una carpeta: sólo sus fondos, a tres por fila. Las cuatro opciones de la
    // portada no se repiten acá —no pertenecen a esta carpeta, y lo que se está haciendo
    // es recorrerla—.
    const deLaCarpeta = carpeta === MIS_FONDOS ? guardados : fondosDeLaCarpeta;
    const titulo =
      carpeta === MIS_FONDOS
        ? 'Mis fondos'
        : carpetas.find((c) => c.id === carpeta)?.label || carpeta;
    return (
      <div className="flex flex-col gap-3 animate-mode-swap">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setVista('lista')}
            className="w-8 h-8 shrink-0 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
            aria-label="Volver"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <p className="text-[16px] font-semibold text-brand-black/90">
            {titulo}
          </p>
          <span className="text-[13px] text-[#3C3C43]/55">
            {deLaCarpeta.length}{' '}
            {deLaCarpeta.length === 1 ? 'fondo' : 'fondos'}
          </span>
        </div>
        <div
          className="grid content-start gap-3"
          style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}
        >
          {carpeta === MIS_FONDOS
            ? guardados.map((f) => (
                <CardFondo
                  key={`guardado-${f.id}`}
                  nombre={f.nombre}
                  imageUrl={f.imageUrl}
                  elegido={
                    selection?.mode === 'guardado' &&
                    selection.fileName === f.fileName
                  }
                  onClick={() =>
                    onChange({
                      mode: 'guardado',
                      fileName: f.fileName,
                      name: f.nombre,
                      imageUrl: f.imageUrl,
                    })
                  }
                />
              ))
            : fondosDeLaCarpeta.map((f) => (
                <CardFondo
                  key={f.id}
                  nombre={f.nombre}
                  imageUrl={f.imageUrl}
                  elegido={
                    selection?.mode === 'biblioteca' && selection.id === f.id
                  }
                  onClick={() =>
                    onChange({
                      mode: 'biblioteca',
                      id: f.id,
                      name: f.nombre,
                      imageUrl: f.imageUrl,
                    })
                  }
                />
              ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {/* La portada: primero las cuatro opciones que NO son una foto de la biblioteca, a
          DOS por fila; abajo, la biblioteca en carpetas.

          Dos por fila y no tres: son cuatro, así que a tres por fila la cuarta quedaba
          sola en su renglón. Arriba van las dos que traen material propio —subir un fondo
          y describirlo—, y abajo las dos que lo resuelven sin nada —Automático y Fondo
          blanco—: lo primero que se ofrece es traer lo tuyo
          (ADR-2026-09-02-subir-y-describir-primero, que supersede el orden de
          ADR-2026-09-02-los-fondos-de-atlas-vuelven-a-carpetas). */}
      <div
        className="grid content-start gap-3"
        style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}
      >
        <CardAccion
          label="Subir un fondo"
          icono={<Upload className="w-4 h-4" />}
          onClick={() => setVista('subir')}
        />
        <CardAccion
          label="Describí tu fondo"
          icono={<TypeIcon className="w-4 h-4" />}
          elegida={selection?.mode === 'descripcion'}
          // Abre la VISTA de describir —como "Subir un fondo"— y no toca la selección: el
          // fondo se elige recién al confirmar ahí adentro
          // (ADR-2026-09-02-describir-fondo-vuelve-al-chat).
          onClick={() => setVista('describir')}
        />
        <button
          type="button"
          onClick={() => onChange({ mode: 'auto' })}
          className="relative h-[var(--alto-fila,150px)] w-full rounded-2xl overflow-hidden flex flex-col items-center justify-center gap-2 text-white shadow-sm transition-transform active:scale-[0.97] bg-brand-primary"
        >
          <AnimatedBlurBg pauseOnDesktopUnselected selected={esAuto} />
          {/* La misma marca que las cards comunes —borde de 2px y check de 24px en la
              esquina—, con el color dado vuelta porque la card ya es azul.
              Ver ADR-2026-08-25-un-solo-check-de-elegida. */}
          {esAuto && (
            <>
              <span className="pointer-events-none absolute inset-0 z-20 rounded-2xl border-2 border-white" />
              <span className="absolute top-2 right-2 z-20 w-6 h-6 rounded-full inline-flex items-center justify-center shadow bg-white text-brand-primary">
                <Check className="w-3.5 h-3.5" strokeWidth={3} />
              </span>
            </>
          )}
          <Sparkles className="relative z-10 w-6 h-6 drop-shadow" />
          <span className="relative z-10 text-[12.5px] font-semibold leading-tight px-2 text-center drop-shadow">
            Automático
          </span>
        </button>
        {/* El fondo liso: blanco por defecto, y con los otros colores elegidos abajo
            (`AjustesFondoBlanco`). La card MUESTRA el color elegido —el swatch se pinta
            y el nombre lo dice— porque "Fondo blanco" fijo mentía apenas alguien elegía
            beige (ADR-2026-09-15-el-fondo-liso-con-color-llega-a-modelo). */}
        <button
          type="button"
          // Re-tocar la opción NO puede perder el color (ni el tono/luz) ya elegidos —
          // mismo criterio que la card del sheet. Con `{ mode: 'blanco' }` a secas,
          // volver a tocar "Fondo beige" lo reseteaba a blanco.
          onClick={() => onChange(alElegirLiso(selection))}
          className={`h-[var(--alto-fila,150px)] w-full rounded-2xl border-2 bg-white transition-colors flex flex-col items-center justify-center gap-2 ${
            selection?.mode === 'blanco'
              ? 'border-brand-primary'
              : 'border-neutral-200 hover:border-brand-primary/40'
          }`}
        >
          <span
            className="w-9 h-9 rounded-full border border-neutral-200"
            style={{ backgroundColor: hexDelLiso(selection) }}
          />
          <span className="text-[12.5px] font-medium text-brand-black/70">
            {etiquetaDelLiso(selection)}
          </span>
        </button>

        {/* El tono y la luz del blanco: el MISMO control que usa el picker de producción
            (`AjustesFondoBlanco`), no una copia. Va condicionado acá afuera —y no adentro
            del componente, que ya devuelve null— porque una celda vacía que ocupa el
            ancho entero igual se come un renglón. Va al pie de la grilla, en el renglón
            siguiente al de la card que lo abre. */}
        {selection?.mode === 'blanco' && (
          <div
            className="min-w-0 self-start animate-mode-swap"
            style={{ gridColumn: '1 / -1' }}
          >
            <AjustesFondoBlanco selection={selection} onChange={onChange} />
          </div>
        )}

      </div>

      {/* La biblioteca de Atlas, en carpetas. Van en su propia grilla y no en la de
          arriba: son otra cosa —material para recorrer, no una decisión de un tap—, y en
          la misma corrida la primera carpeta parecía una quinta opción.

          Con su propio título: las cards de carpeta y las de opción tienen la misma
          medida, así que sin un corte entre medio la primera carpeta se leía como una
          quinta opción más. El título nombra lo que empieza ahí. */}
      {carpetas.length > 0 && (
        <div className="flex flex-col gap-2.5 pt-1">
          <p className="text-[13px] font-bold text-brand-black/90">
            Biblioteca de imágenes
          </p>
          <div
            className="grid content-start gap-3"
            style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}
          >
            {carpetas.map((c) => (
              <CardCarpeta
                key={`carpeta-${c.id}`}
                label={c.label}
                count={c.count}
                tapa={c.tapa}
                tieneLoElegido={carpetaElegida === c.id}
                onClick={() => abrirCarpeta(c.id)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
